import { Injectable } from '@angular/core';
import Config from 'config/config';
import posthog from 'posthog-js';

import { User } from '../classes/models';

/**
 * Bridges the app's auth state into PostHog so events attach to a real person
 * instead of an anonymous session. PostHog itself is initialized in `main.ts`
 * (gated on the `postHog` env config), which runs at bootstrap — before anyone
 * is logged in. Identity therefore can't live in `posthog.init()`; instead
 * AuthService drives this service from `identify()` / `clearSession()`, the
 * same hooks that feed Pendo, Chameleon and Datadog RUM.
 *
 * Like Pendo/Chameleon (and unlike Datadog RUM), VIVE admins and impersonation
 * sessions are excluded so internal activity doesn't pollute analytics.
 * Every PostHog call is guarded: a no-op when PostHog isn't configured for the
 * env, and wrapped in try/catch so analytics can never break the app.
 */
@Injectable({
  providedIn: 'root'
})
export class PosthogIdentityService {
  /**
   * Last user id handed to `posthog.identify()`. Lets `syncUser()` skip a
   * duplicate identify when `identify()` runs again for the same user.
   */
  private lastIdentifiedUserId: number | null = null;

  /** PostHog is only `init()`-ed in `main.ts` when the env config provides it. */
  private readonly isConfigured: boolean = Boolean(
    Config.getEnvironmentConfig().postHog
  );

  /**
   * Reconcile the current auth state with PostHog. Identifies a real
   * (non-admin, non-impersonation) user so their events attach to a stable
   * person id; resets when the session resolves to an excluded user so that
   * internal activity stays anonymous.
   *
   * `isImpersonating` comes from AuthService.isImpersonating(), the server's
   * session record. Do NOT read it off the user: the v3 session's UserDto has
   * no `aliasUser`, so that flag is always unset.
   */
  public syncUser(user: User | null, isImpersonating: boolean): void {
    if (!this.isConfigured) {
      return;
    }

    const accountType = user?.UserAccountType?.name;

    if (user?.id != null && accountType !== 'Admin' && !isImpersonating) {
      if (user.id === this.lastIdentifiedUserId) {
        return;
      }
      // PostHog discourages identifying a different user without a reset in
      // between (e.g. admin → impersonated vendor → back). Clear first.
      if (this.lastIdentifiedUserId !== null) {
        this.safeReset();
      }
      this.lastIdentifiedUserId = user.id;
      try {
        // distinctId is the user id; the numeric ids are also sent as person
        // properties so events can be broken down by either user or org.
        posthog.identify(String(user.id), {
          userId: user.id,
          vendorId: user.VendorId ?? undefined,
          companyId: user.CompanyId ?? undefined,
          name:
            user.fullName ??
            [user.firstName, user.lastName].filter(Boolean).join(' '),
          email: user.email,
          accountType,
          role: user.userRole,
          registrationStatus: user.registrationStatus ?? undefined
        });
      } catch (e) {
        // Analytics must never break the app.
        console.error('PostHog identify failed:', e);
      }
    } else if (this.lastIdentifiedUserId !== null) {
      this.reset();
    }
  }

  /** Clear the PostHog identity so the next session starts a fresh anon id. */
  public reset(): void {
    if (!this.isConfigured) {
      return;
    }
    this.lastIdentifiedUserId = null;
    this.safeReset();
  }

  private safeReset(): void {
    try {
      posthog.reset();
    } catch (e) {
      console.error('PostHog reset failed:', e);
    }
  }
}
